/**
 * Prompts Library page - Browse and use saved prompts
 * Workflow 8.6 from CLAUDE.md
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Menu,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';

import { promptsApi, versionsApi, paiqApi, personaWizApi, PersonaSummary } from '../../services/api';
import { getUserSub } from '../../services/tokenResolver';
import { useDraftAutosave, loadDraft, clearDraft } from '../../hooks/useLocalDraft';

/** localStorage draft key for an in-progress *new* prompt (create flow only). */
const NEW_PROMPT_DRAFT_KEY = 'new-prompt';
import PAIQDialog, { CalculatePromptIQResponse } from '../../components/PAIQDialog';
import PersonaSelector from '../../components/PersonaSelector';
import PublishToLibraryDialog from '../../components/PublishToLibraryDialog';

const PAIQ_DIMENSION_LABELS: Array<[keyof NonNullable<CalculatePromptIQResponse['dimensions']>, string]> = [
  ['firstPrinciplesDepth', 'First Principles Depth'],
  ['cognitiveConstraints', 'Cognitive Constraints'],
  ['crossDomainSynthesis', 'Cross-Domain Synthesis'],
  ['specificity', 'Specificity'],
  ['signalToNoise', 'Signal-to-Noise'],
  ['actionability', 'Actionability'],
  ['critiqueLoop', 'Critique Loop'],
];

/** Read-only PAIQ analysis panel shown inside the prompt view dialog. */
function PAIQAnalysisView({ analysis }: { analysis: CalculatePromptIQResponse }) {
  const dimensions = analysis.dimensions;

  const renderList = (title: string, items: string[] | undefined, color: string) =>
    items && items.length > 0 ? (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color }}>
          {title}
        </Typography>
        <List dense disablePadding>
          {items.map((item, i) => (
            <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start' }}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`• ${item}`} />
            </ListItem>
          ))}
        </List>
      </Box>
    ) : null;

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2">PAIQ Analysis</Typography>
        {analysis.paiqGrade && <Chip size="small" color="info" label={analysis.paiqGrade} />}
      </Box>
      {dimensions &&
        PAIQ_DIMENSION_LABELS.filter(([key]) => dimensions[key]).map(([key, label]) => {
          const dim = dimensions[key]!;
          return (
            <Box key={key} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="caption">{label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dim.score}/5
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(dim.score / 5) * 100}
                sx={{ height: 6, borderRadius: 3 }}
              />
              {dim.reasoning && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {dim.reasoning}
                </Typography>
              )}
            </Box>
          );
        })}
      {renderList('Strengths', analysis.strengths, 'success.main')}
      {renderList('Weaknesses', analysis.weaknesses, 'warning.main')}
      {renderList('Suggested Improvements', analysis.improvements, 'info.main')}
    </Box>
  );
}

interface Prompt {
  entityId: string;
  promptName: string;
  description: string;
  userPrompt: string;
  generatedPrompt: string;
  promptText: string;
  tags: string[];
  isPublic: boolean;
  paiqScore?: number;
  paiqAnalysis?: CalculatePromptIQResponse;
  systemPrompt?: string;
  outputFormat?: string;
  personaRoleContext?: string;
  createdAt: string;
  updatedAt: string;
}

interface PromptVersion {
  versionId: string;
  promptId: string;
  content: string;
  label?: string;
  paiqScore?: number;
  createdAt: string;
}

export default function PromptsLibraryPage() {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [includePublic, setIncludePublic] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPrompt, setMenuPrompt] = useState<Prompt | null>(null);
  const [publishTarget, setPublishTarget] = useState<Prompt | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [promptTab, setPromptTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [dialogTab, setDialogTab] = useState(0);
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // New/Edit prompt form state
  const [newPrompt, setNewPrompt] = useState({
    name: '',
    content: '',
    generatedPrompt: '',
    description: '',
    tags: '',
    isPublic: false,
    systemPrompt: '',
    outputFormat: '',
    personaRoleContext: '',
    paiqScore: undefined as number | undefined,
    paiqAnalysis: undefined as CalculatePromptIQResponse | undefined,
  });
  const [paiqDialogOpen, setPaiqDialogOpen] = useState(false);
  const [optimizedPromptForDialog, setOptimizedPromptForDialog] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [cardOptimizingId, setCardOptimizingId] = useState<string | null>(null);
  const [cardPromptRef, setCardPromptRef] = useState<Prompt | null>(null);

  // Persona state
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>('');
  const [customPersona, setCustomPersona] = useState('');
  const [userId, setUserId] = useState<string | null>(null);

  // System Prompt state
  const [systemPrompts, setSystemPrompts] = useState<string[]>([]);
  const [addSystemPromptDialogOpen, setAddSystemPromptDialogOpen] = useState(false);
  const [newSystemPrompt, setNewSystemPrompt] = useState('');

  // Output Format state - pre-seeded with common formats
  const DEFAULT_OUTPUT_FORMATS = ['Text', 'Bullet List', 'Numbered List', 'Table', 'JSON', 'Markdown', 'XML', 'CSV', 'Code'];
  const [outputFormats, setOutputFormats] = useState<string[]>(DEFAULT_OUTPUT_FORMATS);
  const [addOutputFormatDialogOpen, setAddOutputFormatDialogOpen] = useState(false);
  const [newOutputFormat, setNewOutputFormat] = useState('');

  // Load saved system prompts and output formats from localStorage on mount
  useEffect(() => {
    try {
      const savedSystemPrompts = localStorage.getItem('promptfab_system_prompts');
      if (savedSystemPrompts) {
        setSystemPrompts(JSON.parse(savedSystemPrompts));
      }
      const savedOutputFormats = localStorage.getItem('promptfab_output_formats');
      if (savedOutputFormats) {
        setOutputFormats([...DEFAULT_OUTPUT_FORMATS, ...JSON.parse(savedOutputFormats)]);
      }
    } catch {
      /* ignore parse errors */
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave the in-progress *new* prompt so a mid-session logout (expired
  // token), crash, or accidental navigation doesn't discard the user's work.
  // Only while creating (not editing) and only when there's something to keep.
  const hasDraftContent = Boolean(
    newPrompt.name.trim() || newPrompt.content.trim() || newPrompt.generatedPrompt.trim()
  );
  useDraftAutosave(NEW_PROMPT_DRAFT_KEY, newPrompt, createDialogOpen && !editMode && hasDraftContent);

  // On mount, recover a previously autosaved new-prompt draft (e.g. after a
  // forced re-login) so the user gets their work back instead of a blank form.
  useEffect(() => {
    const draft = loadDraft<typeof newPrompt>(NEW_PROMPT_DRAFT_KEY);
    const d = draft?.data;
    if (d && (d.name?.trim() || d.content?.trim() || d.generatedPrompt?.trim())) {
      setNewPrompt(d);
      setSelectedPersonaId('');
      setCustomPersona(d.personaRoleContext || '');
      setEditMode(false);
      setCreateDialogOpen(true);
      setSnackbar({ open: true, message: 'Recovered your unsaved prompt.' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user ID on mount
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const sub = await getUserSub();
        if (sub) setUserId(sub);
      } catch {
        /* ignore */
      }
    };
    fetchUserId();
  }, []);

  // Fetch personas when create dialog opens
  useEffect(() => {
    const fetchPersonas = async () => {
      if (!createDialogOpen || !userId) return;
      setPersonasLoading(true);
      try {
        const result = await personaWizApi.listPersonas(userId);
        console.log('Loaded personas:', result.data);
        setPersonas(result.data || []);
      } catch (err) {
        console.error('Failed to load personas:', err);
      } finally {
        setPersonasLoading(false);
      }
    };
    fetchPersonas();
  }, [createDialogOpen, userId]);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Show only the 20 most recent prompts (backend sorts descending by CreatedAt).
      const result = await promptsApi.list(includePublic, 20);
      setPrompts(result.data || []);
    } catch (err) {
      setError('Failed to load prompts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [includePublic]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // Get unique tags for filtering
  const allTags = [...new Set(prompts.flatMap((p) => p.tags))];

  const filteredPrompts = prompts.filter((p) => {
    // Tag filter
    if (selectedTag && !p.tags.includes(selectedTag)) return false;
    // Search filter
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      p.promptName.toLowerCase().includes(searchLower) ||
      p.description.toLowerCase().includes(searchLower) ||
      p.tags.some((t) => t.toLowerCase().includes(searchLower))
    );
  });

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard!' });
  };

  const handleView = async (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setDialogTab(0);
    setDialogOpen(true);
    // Load version history
    setVersionsLoading(true);
    try {
      const result = await versionsApi.list(prompt.entityId);
      setVersions(result.data || []);
    } catch (err) {
      console.error('Failed to load versions', err);
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, prompt: Prompt) => {
    setMenuAnchor(event.currentTarget);
    setMenuPrompt(prompt);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuPrompt(null);
  };

  const handleDelete = async () => {
    if (!menuPrompt) return;
    try {
      await promptsApi.delete(menuPrompt.entityId);
      setPrompts(prompts.filter((p) => p.entityId !== menuPrompt.entityId));
      setDeleteDialogOpen(false);
      handleMenuClose();
      setSnackbar({ open: true, message: 'Prompt deleted successfully!' });
    } catch (err) {
      setError('Failed to delete prompt');
      console.error(err);
    }
  };

  const handleCreate = async () => {
    try {
      const tags = newPrompt.tags.split(',').map((t) => t.trim()).filter(Boolean);
      await promptsApi.create({
        name: newPrompt.name,
        content: newPrompt.content,
        generatedPrompt: newPrompt.generatedPrompt,
        description: newPrompt.description,
        systemPrompt: newPrompt.systemPrompt,
        outputFormat: newPrompt.outputFormat,
        personaRoleContext: newPrompt.personaRoleContext,
        paiqScore: newPrompt.paiqScore,
        paiqAnalysis: newPrompt.paiqAnalysis,
        tags,
        isPublic: newPrompt.isPublic,
      });
      setCreateDialogOpen(false);
      setNewPrompt({ name: '', content: '', generatedPrompt: '', description: '', tags: '', isPublic: false, systemPrompt: '', outputFormat: '', personaRoleContext: '', paiqScore: undefined, paiqAnalysis: undefined });
      setSelectedPersonaId('');
      setCustomPersona('');
      setPromptTab(0);
      clearDraft(NEW_PROMPT_DRAFT_KEY);
      setSnackbar({ open: true, message: 'Prompt created successfully!' });
      fetchPrompts();
    } catch (err) {
      setError('Failed to create prompt');
      console.error(err);
    }
  };

  const handleEdit = (prompt: Prompt) => {
    setNewPrompt({
      name: prompt.promptName,
      content: prompt.userPrompt || prompt.promptText || '',
      generatedPrompt: prompt.generatedPrompt || '',
      description: prompt.description || '',
      tags: prompt.tags.join(', '),
      isPublic: prompt.isPublic || false,
      systemPrompt: prompt.systemPrompt || '',
      outputFormat: prompt.outputFormat || '',
      personaRoleContext: prompt.personaRoleContext || '',
      paiqScore: prompt.paiqScore,
      paiqAnalysis: prompt.paiqAnalysis,
    });
    setSelectedPersonaId('');
    // Surface the saved persona context so it's visible/editable on reopen.
    // We only persist the resolved text, not the original persona id, so show
    // it in the custom-persona field rather than the dropdown.
    setCustomPersona(prompt.personaRoleContext || '');
    setSelectedPrompt(prompt);
    setEditMode(true);
    setCreateDialogOpen(true);
    handleMenuClose();
  };

  const handleUpdate = async () => {
    if (!selectedPrompt) return;
    try {
      const tags = newPrompt.tags.split(',').map((t) => t.trim()).filter(Boolean);
      await promptsApi.update(selectedPrompt.entityId, {
        name: newPrompt.name,
        content: newPrompt.content,
        generatedPrompt: newPrompt.generatedPrompt,
        description: newPrompt.description,
        systemPrompt: newPrompt.systemPrompt,
        outputFormat: newPrompt.outputFormat,
        personaRoleContext: newPrompt.personaRoleContext,
        paiqScore: newPrompt.paiqScore,
        paiqAnalysis: newPrompt.paiqAnalysis,
        tags,
        isPublic: newPrompt.isPublic,
      });
      setCreateDialogOpen(false);
      setEditMode(false);
      setSelectedPrompt(null);
      setNewPrompt({ name: '', content: '', generatedPrompt: '', description: '', tags: '', isPublic: false, systemPrompt: '', outputFormat: '', personaRoleContext: '', paiqScore: undefined, paiqAnalysis: undefined });
      setSelectedPersonaId('');
      setCustomPersona('');
      setSnackbar({ open: true, message: 'Prompt updated successfully!' });
      fetchPrompts();
    } catch (err) {
      setError('Failed to update prompt');
      console.error(err);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setEditMode(false);
    setSelectedPrompt(null);
    setNewPrompt({ name: '', content: '', generatedPrompt: '', description: '', tags: '', isPublic: false, systemPrompt: '', outputFormat: '', personaRoleContext: '', paiqScore: undefined, paiqAnalysis: undefined });
    setSelectedPersonaId('');
    setCustomPersona('');
    setPromptTab(0);
    clearDraft(NEW_PROMPT_DRAFT_KEY);
  };

  const handleRestoreVersion = async (version: PromptVersion) => {
    if (!selectedPrompt) return;
    try {
      await promptsApi.update(selectedPrompt.entityId, {
        content: version.content,
      });
      setSnackbar({ open: true, message: 'Version restored successfully!' });
      fetchPrompts();
      // Refresh the view
      const result = await promptsApi.get(selectedPrompt.entityId);
      setSelectedPrompt(result.data);
    } catch (err) {
      setError('Failed to restore version');
      console.error(err);
    }
  };

  const handleOptimize = async () => {
    const sourcePrompt = newPrompt.generatedPrompt || newPrompt.content;
    if (!sourcePrompt) return;
    setIsOptimizing(true);
    setError(null);
    try {
      // Use pre-built personaRoleContext from state
      const personaContext = newPrompt.personaRoleContext || undefined;

      // Step 1: generateSmartPrompt - transforms simple prompt into comprehensive one
      const res = await paiqApi.generateSmartPrompt(sourcePrompt, personaContext);
      const smartPrompt = res.data?.smartPrompt ?? '';
      if (!smartPrompt) {
        throw new Error('generateSmartPrompt returned empty result');
      }
      setOptimizedPromptForDialog(smartPrompt);
      setPaiqDialogOpen(true);
    } catch (err) {
      console.error('Failed to optimize prompt:', err);
      setError(err instanceof Error ? err.message : 'Failed to optimize prompt');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleOptimizeFromCard = async (prompt: Prompt) => {
    const sourcePrompt = prompt.generatedPrompt || prompt.promptText;
    if (!sourcePrompt) return;
    setCardOptimizingId(prompt.entityId);
    setCardPromptRef(prompt);
    setError(null);
    try {
      const res = await paiqApi.generateSmartPrompt(sourcePrompt, prompt.personaRoleContext || undefined);
      const smartPrompt = res.data?.smartPrompt ?? '';
      if (!smartPrompt) throw new Error('generateSmartPrompt returned empty result');
      setOptimizedPromptForDialog(smartPrompt);
      setPaiqDialogOpen(true);
    } catch (err) {
      console.error('Failed to optimize prompt from card:', err);
      setError(err instanceof Error ? err.message : 'Failed to optimize prompt');
    } finally {
      setCardOptimizingId(null);
    }
  };

  const handlePaiqSave = async (optimizedPrompt: string, paiqScore?: number, paiqAnalysis?: CalculatePromptIQResponse) => {
    // Update local state
    setNewPrompt({ ...newPrompt, generatedPrompt: optimizedPrompt, paiqScore, paiqAnalysis });

    // Persist to database if we're editing an existing prompt
    const cardPrompt = cardPromptRef;
    setCardPromptRef(null);
    if (cardPrompt) {
      try {
        await promptsApi.update(cardPrompt.entityId, { generatedPrompt: optimizedPrompt, paiqScore, ...(paiqAnalysis ? { paiqAnalysis } : {}) });
        setSnackbar({ open: true, message: paiqScore ? `Prompt saved! PAIQ Score: ${paiqScore}/35` : 'Optimized prompt saved!' });
        fetchPrompts();
      } catch (err) {
        console.error('Failed to save optimized prompt from card:', err);
        setError('Failed to save optimized prompt');
      }
      return;
    }
    if (selectedPrompt) {
      try {
        await promptsApi.update(selectedPrompt.entityId, {
          generatedPrompt: optimizedPrompt,
          paiqScore,
          ...(paiqAnalysis ? { paiqAnalysis } : {}),
        });
        setSnackbar({ open: true, message: paiqScore ? `Prompt saved! PAIQ Score: ${paiqScore}/35` : 'Optimized prompt saved!' });
        fetchPrompts();
        // Refresh current prompt view
        const result = await promptsApi.get(selectedPrompt.entityId);
        setSelectedPrompt(result.data);
      } catch (err) {
        console.error('Failed to save optimized prompt:', err);
        setError('Failed to save optimized prompt');
      }
    } else {
      // Brand-new prompt in the create dialog: bring the optimized result back
      // into the form on the "Optimized Prompt" tab and prepopulate description
      // + tags, so the user can review and click "Create" to save.
      const firstLine =
        (newPrompt.content || optimizedPrompt)
          .split('\n')
          .map((line) => line.trim())
          .find(Boolean) ?? '';
      const existingTags = newPrompt.tags.split(',').map((t) => t.trim()).filter(Boolean);
      const derivedTags = existingTags.includes('optimized')
        ? newPrompt.tags
        : [...existingTags, 'optimized'].join(', ');
      setNewPrompt((prev) => ({
        ...prev,
        generatedPrompt: optimizedPrompt,
        paiqScore,
        paiqAnalysis,
        description: prev.description.trim() || firstLine.slice(0, 160),
        tags: derivedTags,
      }));
      setPromptTab(1);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Prompts Library
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Browse, search, and manage your saved prompts
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Create Prompt
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search prompts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={includePublic}
              onChange={(e) => setIncludePublic(e.target.checked)}
            />
          }
          label="Include public prompts"
        />
      </Box>

      {/* Tag Filters */}
      {allTags.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 3, flexWrap: 'wrap' }}>
          <Chip
            label="All"
            size="small"
            variant={selectedTag === null ? 'filled' : 'outlined'}
            onClick={() => setSelectedTag(null)}
          />
          {allTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant={selectedTag === tag ? 'filled' : 'outlined'}
              onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
            />
          ))}
        </Box>
      )}

      {/* Prompts Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredPrompts.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No prompts found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {search || selectedTag ? 'Try a different search term or filter' : 'Create your first prompt to get started'}
          </Typography>
          {!search && !selectedTag && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Prompt
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredPrompts.map((prompt) => (
            <Grid item xs={12} sm={6} md={4} key={prompt.entityId}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 },
                }}
                onClick={() => handleView(prompt)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
                      {prompt.promptName}
                    </Typography>
                    <Tooltip title={prompt.isPublic ? 'Public' : 'Private'}>
                      {prompt.isPublic ? (
                        <PublicIcon fontSize="small" color="primary" />
                      ) : (
                        <LockIcon fontSize="small" color="action" />
                      )}
                    </Tooltip>
                  </Box>
                  
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {prompt.description || 'No description'}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'background.default',
                      p: 1,
                      borderRadius: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {prompt.generatedPrompt || prompt.userPrompt || prompt.promptText}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                    {prompt.tags.slice(0, 3).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                    {prompt.tags.length > 3 && (
                      <Chip label={`+${prompt.tags.length - 3}`} size="small" />
                    )}
                  </Box>

                  {prompt.paiqScore && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`PAIQ: ${prompt.paiqScore}/40`}
                        size="small"
                        color={prompt.paiqScore >= 32 ? 'success' : prompt.paiqScore >= 24 ? 'warning' : 'default'}
                      />
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Box>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(prompt.generatedPrompt || prompt.userPrompt || prompt.promptText);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(prompt);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Optimize with PAIQ">
                      <IconButton
                        size="small"
                        disabled={cardOptimizingId === prompt.entityId}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOptimizeFromCard(prompt);
                        }}
                      >
                        {cardOptimizingId === prompt.entityId
                          ? <CircularProgress size={16} />
                          : <AutoFixHighIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, prompt);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (menuPrompt) handleEdit(menuPrompt);
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuPrompt) setPublishTarget(menuPrompt);
          handleMenuClose();
        }}>
          <PublicIcon fontSize="small" sx={{ mr: 1 }} />
          Publish to Library
        </MenuItem>
        <MenuItem
          onClick={() => setDeleteDialogOpen(true)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* View Dialog with Version History */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedPrompt?.promptName}
            <Tooltip title={selectedPrompt?.isPublic ? 'Public' : 'Private'}>
              {selectedPrompt?.isPublic ? (
                <PublicIcon fontSize="small" color="primary" />
              ) : (
                <LockIcon fontSize="small" color="action" />
              )}
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={dialogTab}
            onChange={(_, v) => setDialogTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Prompt" />
            <Tab label="Version History" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>

          {dialogTab === 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedPrompt?.description}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={selectedPrompt?.generatedPrompt || selectedPrompt?.userPrompt || selectedPrompt?.promptText || ''}
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={{ fontFamily: 'monospace' }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                {selectedPrompt?.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" />
                ))}
              </Box>
              {(selectedPrompt?.personaRoleContext || selectedPrompt?.systemPrompt || selectedPrompt?.outputFormat) && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {selectedPrompt?.personaRoleContext && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        Persona
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedPrompt.personaRoleContext}
                      </Typography>
                    </Box>
                  )}
                  {selectedPrompt?.systemPrompt && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        System Prompt
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedPrompt.systemPrompt}
                      </Typography>
                    </Box>
                  )}
                  {selectedPrompt?.outputFormat && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        Output Format
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedPrompt.outputFormat}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {selectedPrompt?.paiqScore && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={`PAIQ Score: ${selectedPrompt.paiqScore}/35`}
                    color={selectedPrompt.paiqScore >= 30 ? 'success' : selectedPrompt.paiqScore >= 26 ? 'warning' : 'default'}
                  />
                </Box>
              )}
              {selectedPrompt?.paiqAnalysis && (
                <PAIQAnalysisView analysis={selectedPrompt.paiqAnalysis} />
              )}
            </>
          )}

          {dialogTab === 1 && (
            <Box>
              {versionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : versions.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                  No version history available
                </Typography>
              ) : (
                <List>
                  {versions.map((version, index) => (
                    <Box key={version.versionId}>
                      <ListItem
                        secondaryAction={
                          <Button
                            size="small"
                            onClick={() => handleRestoreVersion(version)}
                          >
                            Restore
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={version.label || `Version ${versions.length - index}`}
                          secondary={
                            <>
                              <Typography component="span" variant="body2" color="text.secondary">
                                {new Date(version.createdAt).toLocaleString()}
                              </Typography>
                              {version.paiqScore && (
                                <Chip
                                  size="small"
                                  label={`PAIQ: ${version.paiqScore}`}
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </>
                          }
                        />
                      </ListItem>
                      {index < versions.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => handleCopy(selectedPrompt?.generatedPrompt || selectedPrompt?.userPrompt || selectedPrompt?.promptText || '')}
          >
            Copy
          </Button>
          <Button
            color="success"
            startIcon={<PublicIcon />}
            disabled={!selectedPrompt?.entityId}
            onClick={() => {
              if (selectedPrompt) setPublishTarget(selectedPrompt);
            }}
          >
            Publish to Library
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            disabled={!selectedPrompt?.entityId}
            onClick={() => {
              if (selectedPrompt?.entityId) {
                window.location.href = `/optimizer?promptId=${encodeURIComponent(selectedPrompt.entityId)}`;
              }
            }}
          >
            Optimize
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Prompt</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{menuPrompt?.promptName}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        onClose={(_event, reason) => {
          // Only close via explicit Cancel/Save buttons, not backdrop click or escape
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseCreateDialog();
          }
        }}
      >
        <DialogTitle>{editMode ? 'Edit Prompt' : 'Create New Prompt'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={isOptimizing ? <CircularProgress size={18} /> : <AutoFixHighIcon />}
              onClick={handleOptimize}
              disabled={!newPrompt.content || newPrompt.name.length < 3 || isOptimizing}
            >
              {isOptimizing ? 'Optimizing...' : 'Optimize with PAIQ'}
            </Button>
          </Box>
          <TextField
            fullWidth
            label="Name"
            value={newPrompt.name}
            onChange={(e) => setNewPrompt({ ...newPrompt, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
            helperText={newPrompt.name.length > 0 && newPrompt.name.length < 3 ? 'Name must be at least 3 characters to enable optimization' : ''}
            error={newPrompt.name.length > 0 && newPrompt.name.length < 3}
          />

          <Box sx={{ mb: 2 }}>
            <Tabs
              value={promptTab}
              onChange={(_e, v) => setPromptTab(v)}
              sx={{ mb: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0 } }}
            >
              <Tab label="Original Prompt" />
              <Tab label={newPrompt.paiqScore ? `Optimized Prompt (${newPrompt.paiqScore}/35)` : 'Optimized Prompt'} />
            </Tabs>
            {promptTab === 0 ? (
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Prompt Content"
                value={newPrompt.content}
                onChange={(e) => setNewPrompt({ ...newPrompt, content: e.target.value })}
              />
            ) : (
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Optimized Prompt"
                value={newPrompt.generatedPrompt}
                onChange={(e) => setNewPrompt({ ...newPrompt, generatedPrompt: e.target.value })}
                placeholder="Run 'Optimize with PAIQ' to generate an optimized version of your prompt."
                helperText={!newPrompt.generatedPrompt ? 'No optimized prompt yet — click "Optimize with PAIQ" below.' : ''}
              />
            )}
          </Box>

          <PersonaSelector
            personas={personas}
            personasLoading={personasLoading}
            selectedPersonaId={selectedPersonaId}
            customPersona={customPersona}
            personaRoleContext={newPrompt.personaRoleContext}
            onSelectedPersonaIdChange={setSelectedPersonaId}
            onCustomPersonaChange={setCustomPersona}
            onPersonaRoleContextChange={(value) =>
              setNewPrompt((prev) => ({ ...prev, personaRoleContext: value }))
            }
            helperText="Select a persona from PersonaFab.com or enter a custom description to tailor the optimization."
          />

          {/* Output Format Selection */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Output Format (Optional)</InputLabel>
              <Select
                value={newPrompt.outputFormat || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '__ADD_NEW__') {
                    setAddOutputFormatDialogOpen(true);
                  } else {
                    setNewPrompt({ ...newPrompt, outputFormat: value });
                  }
                }}
                label="Output Format (Optional)"
              >
                <MenuItem key="none" value="">
                  <em>None</em>
                </MenuItem>
                {outputFormats.map((fmt, idx) => (
                  <MenuItem key={`fmt-${idx}`} value={fmt}>
                    {fmt}
                  </MenuItem>
                ))}
                <Divider key="divider" />
                <MenuItem key="add-new" value="__ADD_NEW__" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Add Output Format...
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* System Prompt Selection */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>System Prompt (Optional)</InputLabel>
              <Select
                value={newPrompt.systemPrompt || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '__ADD_NEW__') {
                    setAddSystemPromptDialogOpen(true);
                  } else {
                    setNewPrompt({ ...newPrompt, systemPrompt: value });
                  }
                }}
                label="System Prompt (Optional)"
              >
                <MenuItem key="none" value="">
                  <em>None</em>
                </MenuItem>
                {systemPrompts.map((sp, idx) => (
                  <MenuItem key={`sp-${idx}`} value={sp}>
                    {sp.length > 60 ? sp.substring(0, 60) + '...' : sp}
                  </MenuItem>
                ))}
                <Divider key="divider" />
                <MenuItem key="add-new" value="__ADD_NEW__" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Add System Prompt...
                </MenuItem>
              </Select>
            </FormControl>
            {newPrompt.systemPrompt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                {newPrompt.systemPrompt}
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            label="Tags (comma-separated)"
            value={newPrompt.tags}
            onChange={(e) => setNewPrompt({ ...newPrompt, tags: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={newPrompt.isPublic}
                onChange={(e) => setNewPrompt({ ...newPrompt, isPublic: e.target.checked })}
              />
            }
            label="Make this prompt public"
          />
          {!editMode && newPrompt.generatedPrompt && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your original prompt has been optimized (see the “Optimized Prompt” tab). Click the Create button to save this prompt to your library.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={editMode ? <SaveIcon /> : <AddIcon />}
            onClick={editMode ? handleUpdate : handleCreate}
            disabled={!newPrompt.name || !newPrompt.content}
          >
            {editMode ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* PAIQ Dialog */}
      <PAIQDialog
        open={paiqDialogOpen}
        onClose={() => setPaiqDialogOpen(false)}
        initialPrompt={optimizedPromptForDialog}
        onSave={handlePaiqSave}
        systemPrompt={newPrompt.systemPrompt}
        outputFormat={newPrompt.outputFormat}
        personaRoleContext={newPrompt.personaRoleContext}
      />

      {publishTarget && (
        <PublishToLibraryDialog
          open={Boolean(publishTarget)}
          onClose={() => setPublishTarget(null)}
          type="prompt"
          sourceId={publishTarget.entityId}
          defaultTitle={publishTarget.promptName}
          defaultDescription={publishTarget.description}
          defaultTags={publishTarget.tags}
          showPaiqOption={typeof publishTarget.paiqScore === 'number'}
          onPublished={() => setSnackbar({ open: true, message: 'Published to the public library!' })}
        />
      )}

      {/* Add System Prompt Dialog */}
      <Dialog
        open={addSystemPromptDialogOpen}
        onClose={() => setAddSystemPromptDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add System Prompt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a system prompt that will be sent to the LLM to guide its behavior.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="System Prompt"
            value={newSystemPrompt}
            onChange={(e) => setNewSystemPrompt(e.target.value)}
            placeholder="e.g., You are an expert technical writer who creates clear, concise documentation."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddSystemPromptDialogOpen(false); setNewSystemPrompt(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (newSystemPrompt.trim()) {
                const updated = [...systemPrompts, newSystemPrompt.trim()];
                setSystemPrompts(updated);
                localStorage.setItem('promptfab_system_prompts', JSON.stringify(updated));
                setNewPrompt({ ...newPrompt, systemPrompt: newSystemPrompt.trim() });
                setNewSystemPrompt('');
                setAddSystemPromptDialogOpen(false);
              }
            }}
            disabled={!newSystemPrompt.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Output Format Dialog */}
      <Dialog
        open={addOutputFormatDialogOpen}
        onClose={() => setAddOutputFormatDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Output Format</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a custom output format that will be appended to the prompt.
          </Typography>
          <TextField
            fullWidth
            label="Output Format"
            value={newOutputFormat}
            onChange={(e) => setNewOutputFormat(e.target.value)}
            placeholder="e.g., Executive Summary, Step-by-Step Guide"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddOutputFormatDialogOpen(false); setNewOutputFormat(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (newOutputFormat.trim()) {
                const customFormats = outputFormats.filter(f => !DEFAULT_OUTPUT_FORMATS.includes(f));
                const updated = [...customFormats, newOutputFormat.trim()];
                setOutputFormats([...DEFAULT_OUTPUT_FORMATS, ...updated]);
                localStorage.setItem('promptfab_output_formats', JSON.stringify(updated));
                setNewPrompt({ ...newPrompt, outputFormat: newOutputFormat.trim() });
                setNewOutputFormat('');
                setAddOutputFormatDialogOpen(false);
              }
            }}
            disabled={!newOutputFormat.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
